<template>
    <hover-tooltip v-if="props.disabled" :tooltipText="props.disabledText" />
    <spark-multi-select
        ref="multiSelectRef"
        :aria-label="selectedFuelNamesLabel || 'Fuel types'"
        :model-value="selectedMultipleFuelTypes"
        :display="props.hasUpdate ? 'comma' : 'chip'"
        summary-label="fuel"
        option-label="name"
        :disabled="props.disabled"
        :options="fuelTypesToUse"
        :placeholder="props.placeholderOverride || 'Select fuel type'"
        :reset-filter-on-hide="props.resetAfter"
        :filter-fields="['searchQuery']"
        :class="['fuel-selector', { 'full-width': props.fullWidth }]"
        :virtual-scroller-options="{ itemSize: 50 }"
        :show-toggle-all="props.showToggleAll"
        :summary-display-threshold="8"
        @update:model-value="updateMultiValue"
        @hide="onMultiSelectHide"
        @before-show="setIsOpen(true)"
        v-if="props.multiSelect"
    >
        <template #option="slotProps">
            <div class="option-row">
                <div class="option-row-text">
                    {{ slotProps.option.name }}
                </div>
                <icofont-icon
                    background-colour="transparent"
                    :icon-colour="slotProps.option.colour"
                    :icon="slotProps.option.icon"
                    size="large"
                />
            </div>
        </template>
        <template #value="{ value }">
            <div
                class="multiselect-selected"
                v-tooltip.top="{ value: selectedFuelNamesLabel, showDelay: 1000 }"
                :aria-label="selectedFuelNamesLabel"
            >
                <template v-if="value && value.length">
                    <span
                        v-for="item in value"
                        :key="item.id"
                        class="item"
                    >
                        <icofont-icon
                            size="large"
                            :icon-colour="item.colour"
                            :icon="item.icon"
                            aria-hidden
                        />
                    </span>
                </template>
                <template v-else>
                    No technologies selected
                </template>
            </div>
        </template>
        <template #footer v-if="props.hasUpdate">
            <div class="footer">
                <prime-button class="plain-btn" @click="hideOverlay">Cancel</prime-button>
                <prime-button @click="emitValueAndHideModal">Update</prime-button>
            </div>
        </template>
    </spark-multi-select>
    <primevue-select
        :model-value="selectedSingleFuelType"
        auto-filter-focus
        filter
        option-label="name"
        :disabled="props.disabled"
        :options="fuelTypesToUse"
        :placeholder="props.placeholderOverride || 'Select fuel type'"
        :reset-filter-on-hide="props.resetAfter"
        :filter-fields="['searchQuery']"
        :class="['fuel-selector', { 'full-width': props.fullWidth }]"
        :virtual-scroller-options="{ itemSize: 50 }"
        @update:model-value="updateSingleValue"
        @hide="setIsOpen(false)"
        @before-show="setIsOpen(true)"
        v-else
    >
        <template #dropdownicon>
            <font-awesome-icon class="dropdown-icon" :class="{ inverted: isOpen }" :icon="['fas', 'chevron-down']" />
        </template>
        <template #option="slotProps">
            <div class="option-row">
                <div class="option-row-text">
                    {{ slotProps.option.name }}
                </div>
                <icofont-icon
                    background-colour="transparent"
                    :icon-colour="slotProps.option.colour"
                    :icon="slotProps.option.icon"
                    size="large"
                />
            </div>
        </template>
        <template #value="{ value }">
            <div v-if="value" class="option-row">
                <icofont-icon
                    :icon-colour="value.colour"
                    :icon="value.icon"
                    background-colour="transparent"
                    size="small"
                />
                {{ value.name }}
            </div>
        </template>
    </primevue-select>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import PrimevueSelect from 'primevue/select';
import { IFuelType } from '@/interfaces/powerMarkets/IFuelType.ts';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import HoverTooltip from '@/components/powerMarkets/common/HoverTooltip.vue';
import SparkMultiSelect from '@/components/common/SparkMultiSelect.vue';
import PrimeButton from 'primevue/button';
import Popover from 'primevue/popover';
import IcofontIcon from '../common/IcofontIcon.vue';

const dataStore = useDataStore();
type FuelSelectorType = Array<IFuelType> | IFuelType | Array<string> | string | null;

const props = withDefaults(defineProps<{
    modelValue?: FuelSelectorType;
    placeholderOverride?: string;
    customOptionsToHide?: Array<string>;
    fullWidth?: boolean;
    autoWidth?: boolean;
    disabled?: boolean;
    disabledText?: string;
    resetAfter?: boolean;
    multiSelect?: boolean;
    options?: Array<IFuelType>;
    hasUpdate?: boolean;
    showToggleAll?: boolean;
}>(), {
    modelValue: null,
    placeholderOverride: '',
    customOptionsToHide: () => [],
    disabledText: '',
    options: undefined,
});

const emit = defineEmits<{
    'update:model-value-single': [IFuelType];
    'update:model-value-multiple': [Array<IFuelType>];
}>();

const selectedMultipleFuelTypes = ref<Array<IFuelType>>([]);
const selectedSingleFuelType = ref<IFuelType | null>(null);
const isOpen = ref<boolean>(false);
const multiSelectRef = ref<InstanceType<typeof Popover> | null>(null);
const keepPendingSelectionOnHide = ref<boolean>(false);

const fuelTypesToUse = computed<Array<IFuelType>>(() => props.options ?? allFuelTypes.value);

const allFuelTypes = computed<Array<IFuelType>>(() => dataStore.staticStore.fuelTypes.filter((fuelType: IFuelType) => fuelType.searchable && !props.customOptionsToHide.includes(fuelType.id)));

const selectedFuelNamesLabel = computed<string>(() => selectedMultipleFuelTypes.value.map((fuelType: IFuelType) => fuelType.name).join(', '));

function emitValue (): void {
    if (props.multiSelect) {
        emit('update:model-value-multiple', selectedMultipleFuelTypes.value);
    } else {
        emit('update:model-value-single', selectedSingleFuelType.value!);
    }
}

function updateMultiValue (value: Array<IFuelType>): void {
    selectedMultipleFuelTypes.value = value;
    if (props.hasUpdate) return;
    emitValue();
}

function updateSingleValue (value: IFuelType | null): void {
    selectedSingleFuelType.value = value;
    if (props.hasUpdate) return;
    emitValue();
}

function emitValueAndHideModal (): void {
    keepPendingSelectionOnHide.value = true;
    emitValue();
    hideOverlay();
}

function hideOverlay (): void {
    if (!multiSelectRef.value) return;
    multiSelectRef.value.hide();
}

function setIsOpen (value: boolean): void {
    isOpen.value = value;
}

function onMultiSelectHide (): void {
    setIsOpen(false);
    if (props.hasUpdate) {
        if (!keepPendingSelectionOnHide.value) {
            syncSelectedFuelTypesFromModel();
        }
        keepPendingSelectionOnHide.value = false;
        return;
    }
    if (!props.hasUpdate && props.multiSelect) {
        emitValue();
    }
}

function getStringArray (value: FuelSelectorType): Array<string> {
    if (Array.isArray(value) && value.every(val => typeof val === 'string')) return value;
    return [];
}

function syncSelectedFuelTypesFromModel (): void {
    if (!props.modelValue) return;

    if (props.multiSelect && Array.isArray(props.modelValue)) {
        if (props.modelValue.every(val => typeof val === 'string')) {
            selectedMultipleFuelTypes.value = allFuelTypes.value.filter((fuelType: IFuelType) => getStringArray(props.modelValue).includes(fuelType.id) || getStringArray(props.modelValue).includes(fuelType.name));
        } else {
            const foundFuels: Array<IFuelType> = [];
            props.modelValue.forEach((fuelType) => {
                if (typeof fuelType === 'string') return;
                const foundFuel: IFuelType | undefined = allFuelTypes.value.find((fuel: IFuelType) => fuel.id === fuelType.id);
                if (foundFuel) foundFuels.push(foundFuel);
            });
            selectedMultipleFuelTypes.value = foundFuels;
        }
    } else {
        selectedSingleFuelType.value = allFuelTypes.value.find((fuelType: IFuelType) => fuelType.id === props.modelValue) ?? null;
    }
}

watch(() => props.modelValue, syncSelectedFuelTypesFromModel, { immediate: true });
</script>

<style lang="scss" scoped>
.fuel-selector {
    width: auto;

    @include responsive('sm') {
        width: 100%;
    }

    &.full-width {
        width: 100%;
    }
}

.multiselect-selected {
    display: flex;
    padding: var(--smallSpacing) 0;

    .item {
        margin: -0.8rem 0;
    }
}

.option-row {
    display: flex;
    gap: var(--defaultSpacing);
    align-items: center;
    width: 100%;

    .option-row-text {
        margin-right: auto;
        margin-left: var(--smallSpacing);
    }
}

.dropdown-icon {
    font-size: var(--smallFontSize);

    &.inverted {
        transform: rotate(180deg);
    }
}

.footer {
    background-color: var(--neutral100);
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: var(--defaultSpacing);
    border-radius: var(--borderRadiusOuter);
}

</style>
